import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageContext";

const FALLBACK = {
  "business-consultancy": "https://static.wixstatic.com/media/11062b_78f78a3902974005b16a24114ad52453~mv2.jpg/v1/fill/w_1200,h_900,al_c,q_85,usm_0.66_1.00_0.01/11062b_78f78a3902974005b16a24114ad52453~mv2.jpg",
  "property-development": "https://static.wixstatic.com/media/11062b_3cc314074dec4cd5891b7bbb0c0b6f12~mv2.jpg/v1/fill/w_1200,h_900,al_c,q_85,usm_0.66_1.00_0.01/11062b_3cc314074dec4cd5891b7bbb0c0b6f12~mv2.jpg",
  "transaction-support": "https://static.wixstatic.com/media/6765ec_db5106dd5e264fd1bc5dabac07f98d1b~mv2.png/v1/fill/w_1200,h_900,al_c,q_85,usm_0.66_1.00_0.01/6765ec_db5106dd5e264fd1bc5dabac07f98d1b~mv2.png",
};
const IMG_KEY = { "business-consultancy": "service_01", "property-development": "service_02", "transaction-support": "service_03" };

export default function ServiceDetail() {
  const { id } = useParams();
  const { t, lang, images } = useLanguage();
  const service = t.services.list.find((s) => s.id === id);
  const seo = t.seo.servicePages?.[id];

  useEffect(() => {
    if (!seo) return;
    document.title = seo.title;
    const d = document.head.querySelector('meta[name="description"]');
    if (d) d.setAttribute("content", seo.desc);
    const c = document.head.querySelector('link[rel="canonical"]');
    if (c) c.setAttribute("href", `https://horizonadvisorscy.com/services/${id}`);
    window.scrollTo(0, 0);
  }, [seo, id, lang]);

  if (!service) return <Navigate to="/services" replace />;

  return (
    <main className="pt-[72px]" data-testid={`service-page-${id}`}>
      <section className="bg-[#142D44] py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Link to="/services" data-testid="back-to-services" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#9AA6B5] hover:text-[#B85D2A] transition-colors mb-8">
            <ArrowLeft size={13} /> {t.services.backToServices}
          </Link>
          <div className="w-14 h-1.5 bg-[#B85D2A] mb-6" />
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#FBF9F5] leading-[1.05] max-w-3xl">
            <MaskedLines lines={[service.title]} />
          </h1>
          <p className="mt-6 text-[#9AA6B5] text-lg leading-relaxed max-w-2xl">{service.tagline}</p>
        </div>
      </section>

      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-[#4A5568] leading-relaxed text-lg">{service.desc}</p>
              <p className="mt-10 font-mono text-xs uppercase tracking-[0.22em] text-[#8C6B4F] font-semibold">{t.services.deliverablesLabel}</p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-4">
                {service.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm text-[#4A5568] bg-white border border-[#E3DED4] border-l-4 border-l-[#B85D2A] rounded-lg p-4">
                    <Check size={16} className="text-[#B85D2A] mt-0.5 shrink-0" strokeWidth={2} />
                    {d}
                  </li>
                ))}
              </ul>
              <div className="mt-10 bg-[#EFF4F8] border border-[#D8E2EC] rounded-xl p-7">
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#8C6B4F] font-semibold">{t.services.whoLabel}</p>
                <p className="mt-3 text-[#4A5568] leading-relaxed">{service.who}</p>
              </div>
              <Link to="/contact" data-testid={`service-page-cta-${id}`} className="group mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#1E3A5F] text-[#FBF9F5] text-sm font-medium hover:bg-[#B85D2A] transition-colors duration-300">
                {t.services.enquire}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-4 lg:col-start-9">
            <div className="sticky top-28">
              <div className="overflow-hidden rounded-t-2xl aspect-[4/5]">
                <img src={images[IMG_KEY[id]] || FALLBACK[id]} alt={service.title} className="w-full h-full object-cover" />
              </div>
              <div className="bg-[#B85D2A] text-white p-6 rounded-b-2xl">
                <p className="font-serif text-3xl">{service.number}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em]">Horizon Advisors · Cyprus</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
